'use client'

import { useQuery } from '@apollo/client/react'
import { useParams, useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { createContainer } from 'unstated-next'

import {
  Media,
  fromMd,
  fromXl,
  mdStartPixels,
  pageLayoutMaxWidthPixels,
} from '@syconium/little-miss-figgy'

import NotFound from '../../../../(shop)/not-found'
import { singleQueryParamValue } from '../../../../../lib/utils/next'
import { useTrackPageView } from '../../../../_providers/TrackingProvider.client'

import { PdpRegionLocked } from './PdpRegionLocked'
import { PRODUCT_PAGE_QUERY, ProductPageProps } from './ProductPage.config'

import type { GetProductPageDataQuery } from '../../../../../__generated__/graphql/catalog/graphql'

export const ProductPage = ({
  initialData,
  children,
  ...props
}: AwaitedProductPageProps & {
  children?: React.ReactNode
  initialData: GetProductPageDataQuery | undefined
}) => {
  return (
    <ProductPagePropsProvider {...props}>
      <ProductPageImpl initialData={initialData}>{children}</ProductPageImpl>
    </ProductPagePropsProvider>
  )
}

const ProductPageImpl = ({
  initialData,
  children,
}: {
  children?: React.ReactNode
  initialData: GetProductPageDataQuery | undefined
}) => {
  const {
    params: { productHandle },
    searchParams: { color, fit, size },
  } = useProductPageProps()

  const {
    data: currentData,
    loading,
    previousData,
  } = useQuery(PRODUCT_PAGE_QUERY, {
    variables: {
      product: productHandle,
      color,
      fit,
      size,
    },
  })

  const data = currentData ?? previousData ?? initialData

  const { trackPageView } = useTrackPageView()

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (data && !loading) {
      const vg = data.productCatalogStandardProduct.variantGroup

      const selectedVariant = vg.variant ?? null
      const productShopifyGuid = selectedVariant?.shopifyProductId ?? vg.defaultShopifyProduct.id

      const availability = selectedVariant?.availability ?? vg.availability
      const isAvailable = availability === 'AVAILABLE'

      const origin = typeof window !== 'undefined' ? window.location.origin : ''

      trackPageView({
        pageViewName: vg.title,
        pageViewHandle: data.productCatalogStandardProduct.handle,
        pageViewCategory: 'product',
        productShopifyId: selectedVariant?.shopifyProductId ?? vg.defaultShopifyProduct.id,
        iterable: {
          // Note that these properties are used by external teams at FIGS. If there are changes that alter the data getting passed through,
          // please communicate it with relevant stakeholders.
          type: 'trackPdpView' as const,
          payload: {
            product: {
              handle: data.productCatalogStandardProduct.handle,
              title: vg.title,
              variantGroup: {
                shopifyGuid: productShopifyGuid,
                gender: vg.gender,
                category: vg.primaryCategory.id,
                variant: {
                  imageUrl: vg.images?.[0]?.url ?? '',
                  webUrl: vg.urlPath ? `${origin}${vg.urlPath}` : '',
                  color: vg.color?.id ?? '',
                  fit: fit,
                  size: size,
                  isAvailable,
                  shopifyGuid: selectedVariant?.shopifyId ?? '',
                },
              },
            },
          },
        },
      })
    }
  }, [data, fit, loading, size, trackPageView])

  if (!data?.productCatalogStandardProduct) {
    return <NotFound />
  }

  const vg = data.productCatalogStandardProduct.variantGroup
  if (vg.availability === 'UNAVAILABLE_IN_REGION') {
    return <PdpRegionLocked genderCategory={vg.gender} categoryId={vg.primaryCategory.id} />
  }

  return children
}

type AwaitedProductPageProps = {
  params: Awaited<ProductPageProps['params']>
  searchParams: Awaited<ProductPageProps['searchParams']>
}

function useProductPagePropsImpl(props?: AwaitedProductPageProps) {
  const searchParams = useSearchParams()
  const params = useParams()
  return useMemo(() => {
    return {
      params: {
        productHandle: singleQueryParamValue(
          params?.productHandle ?? props?.params.productHandle,
          ''
        ),
        collectionHandle: singleQueryParamValue(
          params?.collectionHandle ?? props?.params.collectionHandle
        ),
      },
      searchParams: {
        color: singleQueryParamValue(searchParams?.get('color') ?? props?.searchParams.color, ''),
        fit: singleQueryParamValue(searchParams?.get('fit') ?? props?.searchParams.fit, ''),
        size: singleQueryParamValue(searchParams?.get('size') ?? props?.searchParams.size, ''),
        fitModel: singleQueryParamValue(
          searchParams?.get('fitModel') ?? props?.searchParams.fitModel
        ),
      },
    }
  }, [
    params?.collectionHandle,
    params?.productHandle,
    props?.params.collectionHandle,
    props?.params.productHandle,
    props?.searchParams.color,
    props?.searchParams.fit,
    props?.searchParams.fitModel,
    props?.searchParams.size,
    searchParams,
  ])
}

const ProductPagePropsContainer = createContainer(useProductPagePropsImpl)
export const ProductPagePropsProvider = ({
  children,
  ...props
}: AwaitedProductPageProps & {
  children?: React.ReactNode
}) => {
  return (
    <ProductPagePropsContainer.Provider initialState={props}>
      {children}
    </ProductPagePropsContainer.Provider>
  )
}
export const useProductPageProps = ProductPagePropsContainer.useContainer

export const ProductPageSection = styled.section`
  max-width: ${pageLayoutMaxWidthPixels}px;
  margin-left: auto;
  margin-right: auto;
`

export const ProductPagePrimaryAndAsideSection = styled(ProductPageSection)<{
  $reserveSpace: boolean
  $useCenteredLayoutForXl?: boolean
}>`
  display: grid;
  grid-template-columns: minmax(min(100%, ${mdStartPixels}px), 1fr);
  ${o => (o.$reserveSpace ? `row-gap: ${o.theme.spacing(6)}` : null)};
  column-gap: ${o => o.theme.spacing(6)};
  margin-bottom: ${props => props.theme.spacing(5)};

  ${fromMd} {
    margin-bottom: ${props => props.theme.spacing(10)};
    grid-template-columns: minmax(0px, 2fr) minmax(0px, 1fr);
    padding-left: ${props => props.theme.spacing(10)};
    padding-right: ${props => props.theme.spacing(10)};
  }
  ${fromXl} {
    ${o =>
      o.$useCenteredLayoutForXl &&
      css`
        grid-template-columns: auto auto;
        justify-content: center;
        column-gap: ${o => o.theme.spacing(10)};
      `}
  }
`

export const ProductPagePrimaryColumnDiv = styled.div`
  grid-column: span 1;
`

export const ProductPageAsideColumnDiv = styled.div`
  grid-column: span 1;
`

export const ProductPageBreadcrumbsRow = styled.div`
  grid-column: 1 / -1;
`

export const ProductPageMobileReviewsAndHighlightsWrapper = styled(Media)<{
  $withBottomMargin: boolean
}>`
  ${o => (o.$withBottomMargin ? `margin-bottom: ${o.theme.spacing(3)};` : '')}
`

export const ProductPageDetailsSectionSidePaddedSection = styled.div`
  margin: 0 ${o => o.theme.dimensions.layout.sidePadding.md}px;

  ${fromMd} {
    margin: 0;
  }
`
